import React, { useEffect, useState, useMemo, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { UserContext } from "../../utils/userContext";
import { isSuperAdmin, isCustomerAdmin } from "../../utils/roles";
import PageHeader from "../../components/ui/PageHeader";
import PageWrapper from "../../components/ui/PageWrapper";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import NewItemButton from "../../components/ui/NewItemButton";
import StatusBadge from "../../components/ui/StatusBadge";
import AdminBreadcrumb from "../../components/admin/AdminBreadcrumb";
import AdminSearchableList from "../../components/admin/AdminSearchableList";
import CustomerBillingDashboard from "../../components/admin/CustomerBillingDashboard";
import TimeReportingDashboard from "../../components/admin/TimeReportingDashboard";
import FormModal from "../../components/admin/FormModal";
import CustomerForm from "../../components/admin/CustomerForm";
import OrganisationForm from "../../components/admin/OrganisationForm";

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orgSearch, setOrgSearch] = useState("");
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  const [newOrgOpen, setNewOrgOpen] = useState(false);

  const canManage =
    isSuperAdmin(user) ||
    (isCustomerAdmin(user) && String(user.activeCustomerId) === String(customerId));

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/admin/customers/${customerId}`);
      setData(res.data);
    } catch (err) {
      setError("Failed to load customer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const organisations = useMemo(() => {
    const orgs = data?.organisations || data?.companies || [];
    const q = orgSearch.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter(
      (o) =>
        (o.name || "").toLowerCase().includes(q) ||
        (o.slug || "").toLowerCase().includes(q)
    );
  }, [data, orgSearch]);

  if (loading) {
    return (
      <PageWrapper>
        <p>Loading…</p>
      </PageWrapper>
    );
  }

  if (error || !data?.customer) {
    return (
      <PageWrapper>
        <p className="text-red-600">{error || "Customer not found"}</p>
      </PageWrapper>
    );
  }

  const { customer } = data;
  const activeOrgId = user?.activeOrganisationId || user?.activeCompanyId;

  const orgColumns = [
    {
      key: "name",
      label: "Name",
      render: (o) => (
        <span className="text-blue-600 font-medium">
          {o.name}
          {String(o._id) === String(activeOrgId) && (
            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
              Current
            </span>
          )}
        </span>
      ),
    },
    { key: "slug", label: "Slug" },
    {
      key: "status",
      label: "Status",
      render: (o) => <StatusBadge status={o.status || "active"} />,
    },
    {
      key: "memberCount",
      label: "Members",
      render: (o) => o.memberCount ?? "—",
    },
  ];

  return (
    <>
      <PageHeader
        title={customer.name}
        breadcrumb={
          <AdminBreadcrumb
            items={[
              { label: "Customers", to: isSuperAdmin(user) ? "/admin/customers" : undefined },
              { label: customer.name },
            ]}
          />
        }
        search={{
          value: orgSearch,
          onChange: (e) => setOrgSearch(e.target.value),
          placeholder: "Search departments",
          onClear: () => setOrgSearch(""),
        }}
        actions={
          canManage ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditCustomerOpen(true)}>
                Edit customer
              </Button>
              <NewItemButton
                onClick={() => setNewOrgOpen(true)}
                label="New department"
                shortLabel="New"
              />
            </div>
          ) : null
        }
      />
      <PageWrapper>
        <TimeReportingDashboard scope="customer" scopeId={customerId} />
        <CustomerBillingDashboard customerId={customerId} />

        <Card className="mb-6 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Status: </span>
              <StatusBadge status={customer.status || "active"} />
            </div>
            <div>
              <span className="text-gray-500">Billing email: </span>
              {customer.billingEmail || "—"}
            </div>
            <div>
              <span className="text-gray-500">Contact: </span>
              {customer.contactName || "—"}
              {customer.contactPhone ? ` · ${customer.contactPhone}` : ""}
            </div>
            {customer.notes && (
              <div className="sm:col-span-2">
                <span className="text-gray-500">Notes: </span>
                {customer.notes}
              </div>
            )}
          </div>
        </Card>

        <h3 className="text-lg font-semibold mb-3">
          Departments · {organisations.length}
        </h3>
        <AdminSearchableList
          items={organisations}
          columns={orgColumns}
          onRowClick={(o) =>
            navigate(`/admin/customers/${customerId}/organisations/${o._id}`)
          }
          emptyMessage="No departments found."
        />
      </PageWrapper>

      <FormModal
        isOpen={editCustomerOpen}
        onClose={() => setEditCustomerOpen(false)}
        title="Edit customer"
      >
        <CustomerForm
          initialValues={customer}
          onSubmit={async (values) => {
            await axiosInstance.put(`/admin/customers/${customerId}`, values);
            setEditCustomerOpen(false);
            fetchData();
          }}
          onCancel={() => setEditCustomerOpen(false)}
        />
      </FormModal>

      <FormModal
        isOpen={newOrgOpen}
        onClose={() => setNewOrgOpen(false)}
        title="New department"
      >
        <OrganisationForm
          onSubmit={async (values) => {
            await axiosInstance.post("/admin/organisations", {
              ...values,
              customerId,
            });
            setNewOrgOpen(false);
            fetchData();
          }}
          onCancel={() => setNewOrgOpen(false)}
          submitLabel="Create department"
        />
      </FormModal>
    </>
  );
}
